import { collarMaterialTokens } from "@/lib/totoMaterialTheme";
import AppLoadingShell from "@/components/AppLoadingShell";
import { usePhoneExperience, largerScreenOnly } from "@/lib/phoneExperience";
import "./PhoneExperience.css";
import React from "react";
import { frontDeskRouteAllowed } from "@/lib/frontDeskAccess";
import { boutiqueWallpaperCode } from "@/hooks/useBoutiqueWallpaper";
import { readAppearance } from "@/lib/appearance";
import "@/styles/toto-appearance.css";
import "@/styles/toto-approved-batch.css";
import "@/styles/toto-vessel-shadows.css";
import "@/styles/toto-vessel-layout.css";
import "@/styles/toto-surface-geometry.css";
import "@/styles/toto-navigation-language.css";
import { selectedNatureTheme } from "@/lib/natureThemes";

import { VesselNavigationProvider } from "@/components/toto/VesselNavigation";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/Sidebar";
import { MobileNavProvider } from "@/context/MobileNavContext";
import TotoNavigationGlyph from "@/components/TotoNavigationGlyph";
import spatialHorizonBright from "@/assets/toto-spatial-horizon-v2.png";
import spatialHorizonCalm from "@/assets/toto-spatial-horizon-v1.png";

const BOUTIQUE_ENVIRONMENT_TINTS = {
  PLT: "77 143 93",
  FLL: "83 119 155",
  WYN: "202 112 164",
  PNC: "190 111 54",
  RNO: "194 155 49",
  CC: "170 63 63",
};

function environmentStyleFor(user) {
  // Shared wallpaper selection survives route remounts and is visual only.
  let selected = null;
  try { selected = window.localStorage.getItem("toto-wallpaper-boutique"); } catch { /* Optional storage. */ }
  const code = selected === "RAINBOW" || BOUTIQUE_ENVIRONMENT_TINTS[selected]
    ? selected : boutiqueWallpaperCode(user);
  const backgroundPackage = window.localStorage.getItem("toto-background-package") || "boutique";
  const nature = selectedNatureTheme(backgroundPackage, user);
  const frost = readAppearance() === "frost";
  const background = nature?.image || (backgroundPackage === "calm" ? spatialHorizonCalm : backgroundPackage === "bright" ? spatialHorizonBright : backgroundPackage === "plain" ? null : "/backgrounds/sonmi-mist-forest.webp");
  return {
    "--toto-boutique-rgb": BOUTIQUE_ENVIRONMENT_TINTS[code] || "116 112 124",
    "--toto-boutique-wash": "0",
    ...collarMaterialTokens(user),
    "--toto-frost-vessel-fill": "linear-gradient(145deg,#f4f1e9,#e9e5dc)",
    "--toto-smoke-vessel-fill": "linear-gradient(145deg,rgba(24,31,34,.96),rgba(18,25,28,.95))",
    "--toto-vessel-fill": frost ? "var(--toto-frost-vessel-fill)" : "var(--toto-smoke-vessel-fill)",
    "--toto-dock-fill": "var(--toto-vessel-fill)",
    "--toto-environment": background ? `url(${background})` : "none",
  };
}

export default function ProtectedLayout({ children, navigationKey }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const phone = usePhoneExperience();
  const canvasRef = React.useRef(null);
  const [, setBackgroundRevision] = React.useState(0);

  React.useEffect(() => {
    const refreshBackground = () => setBackgroundRevision((revision) => revision + 1);
    window.addEventListener("toto-boutique-wallpaper-changed", refreshBackground);
    window.addEventListener("toto-background-package-changed", refreshBackground);
    window.addEventListener("toto-appearance-changed", refreshBackground);
    window.addEventListener("storage", refreshBackground);
    const scheme = window.matchMedia?.("(prefers-color-scheme: dark)");
    scheme?.addEventListener?.("change", refreshBackground);
    return () => {
      window.removeEventListener("toto-boutique-wallpaper-changed", refreshBackground);
      window.removeEventListener("toto-background-package-changed", refreshBackground);
      window.removeEventListener("toto-appearance-changed", refreshBackground);
      window.removeEventListener("storage", refreshBackground);
      scheme?.removeEventListener?.("change", refreshBackground);
    };
  }, []);

  if (loading) {
    return <AppLoadingShell />;
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (!frontDeskRouteAllowed(user, location.pathname)) {
    return <Navigate to="/profile" replace />;
  }

  if (phone && largerScreenOnly(location.pathname)) {
    return <main className="phone-experience" aria-labelledby="phone-experience-title">
      <section><h1 id="phone-experience-title">Available on larger screens</h1>
        <p>Open Workspaces and Hiring on a tablet or computer. Today and Team are available on your phone.</p>
        <nav aria-label="Phone navigation"><Link to="/home">Today</Link><Link to="/people">Team</Link></nav>
      </section>
    </main>;
  }

  // Spatial pages own the full canvas. Unmigrated operational routes retain
  // their existing shell until their navigation and task flow are replaced.
  const isHomeSurface = location.pathname === "/home" || location.pathname.startsWith("/home/");
  const isWorkspaceSurface = location.pathname === "/workspaces" || location.pathname.startsWith("/workspaces/");
  const isSchedulingVessel = /^\/(scheduling\/(mt|coverage|time-off|zenoti|swaps)(\/|$)|help-a-teammate(\/|$))/.test(location.pathname);
  const isSpatialCanvas = isHomeSurface
    || isSchedulingVessel
    || location.pathname === "/today/team"
    || location.pathname === "/today/goals"
    || location.pathname === "/my-day"
    || location.pathname === "/people"
    || location.pathname === "/calendar"
    || isWorkspaceSurface
    || location.pathname === "/sonmi/atrium"
    || location.pathname === "/scheduling"
    || location.pathname.startsWith("/scheduling/eg")
    || location.pathname === "/scheduling/time-off"
    || location.pathname === "/scheduling/preferences"
    || location.pathname === "/eod"
    || location.pathname === "/operations"
    || location.pathname.startsWith("/operations/")
    || (location.pathname === "/documents" || location.pathname.startsWith("/documents/"))
    || location.pathname === "/reviews"
    || location.pathname === "/payroll/prepare"
    || location.pathname === "/payroll/readiness"
    || location.pathname === "/payroll/handoff"
    || location.pathname.startsWith("/payroll/reports/")
    || location.pathname.startsWith("/payroll/calculators/")
    || location.pathname === "/reviews"
    || location.pathname === "/profile"
    || location.pathname === "/settings/connections/zenoti"
    || location.pathname === "/settings/program-audit"
    || location.pathname === "/settings/backgrounds"
    || location.pathname === "/settings/floor";
  const hasLocalSpatialNavigation = location.pathname === "/home/toto" || location.pathname === "/today/team"
    || location.pathname === "/today/goals"
    || location.pathname === "/my-day" || location.pathname === "/people"
    || location.pathname === "/calendar"
    || isWorkspaceSurface
    || isSchedulingVessel
    || location.pathname === "/profile"
    || location.pathname === "/settings/connections/zenoti"
    || location.pathname === "/settings/program-audit"
    || location.pathname === "/settings/backgrounds"
    || location.pathname === "/settings/floor"
    || location.pathname === "/scheduling"
    || location.pathname.startsWith("/scheduling/eg")
    || location.pathname === "/payroll/prepare"
    || location.pathname === "/payroll/readiness"
    || location.pathname === "/payroll/handoff"
    || location.pathname.startsWith("/payroll/reports/")
    || location.pathname.startsWith("/payroll/calculators/")
    || location.pathname === "/eod"
    || location.pathname === "/operations"
    || location.pathname.startsWith("/operations/")
    || (location.pathname === "/documents" || location.pathname.startsWith("/documents/"))
    || location.pathname === "/reviews";
  const environmentStyle = environmentStyleFor(user);
  if (isSpatialCanvas) return <VesselNavigationProvider key={navigationKey || location.pathname}><div ref={canvasRef} className="toto-environment toto-spatial-canvas" data-material="sculpted" data-appearance={readAppearance()} data-documents-pilot={location.pathname.startsWith("/documents") ? "true" : undefined} style={environmentStyle}>

    {location.pathname !== "/home" && !hasLocalSpatialNavigation && <Link className="toto-home-ornament" to="/home" aria-label="Return to TOTO home">
      <span className="toto-home-ornament__surface"><TotoNavigationGlyph name="toto" size={28} /></span>
      <span>TOTO</span>
    </Link>}
    {children}

  </div></VesselNavigationProvider>;

  return <MobileNavProvider><div className="flex min-h-screen" data-testid="app-shell"><Sidebar /><main className="toto-environment flex-1 min-w-0" style={environmentStyle}>{children}</main></div></MobileNavProvider>;
}
